import { Badge, Box, Link, SpaceBetween } from '@cloudscape-design/components';
import type { EvaluationResult } from '@amzn/helianthus-arbiter-client';
import { infoPopover } from './InfoPopover';

const APPSEC_SEVERITY_URL = 'https://w.amazon.com/bin/view/AWS_IT_Security/AppSec/FindingSeverity/';

/** Marks a value the API flags @unstable: shown for evaluation, not to be relied on. */
export function ExperimentalBadge() {
  return <Badge color="blue">Experimental</Badge>;
}

const ASSIGNED_SEVERITY_INFO = (
  <SpaceBetween size="xs">
    <span>
      A model rated this finding&apos;s severity from the change itself, using the AppSec finding
      severity definitions. It can differ from the rule&apos;s severity.
    </span>
    <span>
      This is experimental and still being calibrated. It does not affect the evaluation&apos;s
      outcome; use the rule severity for decisions.
    </span>
    <Link href={APPSEC_SEVERITY_URL} external>
      AppSec finding severity
    </Link>
  </SpaceBetween>
);

interface AssignedSeverityProps {
  result: EvaluationResult;
}

/** The experimental per-finding severity and its rationale, or null when the finding was not rated. */
export function AssignedSeverity({ result }: AssignedSeverityProps): React.JSX.Element | null {
  if (!result.assignedSeverity) return null;
  return (
    <SpaceBetween size="xxs">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <span>{result.assignedSeverity}</span>
        <ExperimentalBadge />
        {infoPopover('Assessed severity', ASSIGNED_SEVERITY_INFO)}
      </SpaceBetween>
      {result.severity && (
        <Box variant="small" color="text-body-secondary">
          Rule severity: {result.severity}
        </Box>
      )}
      {result.assignedSeverityRationale && (
        <Box variant="small" color="text-body-secondary">
          {result.assignedSeverityRationale}
        </Box>
      )}
    </SpaceBetween>
  );
}
